<script setup lang="ts">
import { Header, Footer } from '@/components/ui';
import { convertUrl } from '@/utils/convertUrl';
import { useSessionRePaySimulation } from '@/functions/borrow/simulation/views/composables';
import { ROUTES } from '@/enums';

import { useSession, computed, watch } from '#imports';
import { navigateTo } from '#app';

const { mutateAsync: postSessionRePaySimulation, isLoading: sessionRePaySimulationLoading } =
  useSessionRePaySimulation();

const { session } = await useSession();

// 金額のフォーマット関数
const formattedAmount = computed(() => {
  if (session.value?.rePaySimulationInfo?.hopeAmount) {
    return new Intl.NumberFormat('ja-JP').format(session.value.rePaySimulationInfo.hopeAmount);
  }
});

// 利率のフォーマット関数
const formattedRate = computed(() => {
  if (session.value?.rePaySimulationInfo?.interestRate) {
    const rate = session.value.rePaySimulationInfo.interestRate;
    return rate % 1 === 0 ? `${rate}.0` : rate.toString();
  }
});

// 月々の返済額をフォーマット
const formattedMonthlyRepaymentAmount = computed(() => {
  if (session.value?.rePaySimulationInfo?.monthlyRepaymentAmount !== undefined) {
    return new Intl.NumberFormat('ja-JP').format(session.value.rePaySimulationInfo.monthlyRepaymentAmount);
  }
});

// 総返済額をフォーマット
const formattedTotalRepaymentAmount = computed(() => {
  if (session.value?.rePaySimulationInfo?.repaymentTtl !== undefined) {
    return new Intl.NumberFormat('ja-JP').format(session.value.rePaySimulationInfo.repaymentTtl);
  }
});

// 利息のフォーマット
const formattedInterest = computed(() => {
  if (session.value?.rePaySimulationInfo?.interest !== undefined) {
    return new Intl.NumberFormat('ja-JP').format(session.value.rePaySimulationInfo.interest);
  }
});

const handleClickToApplyNow = async () => {
  const resetData = {
    hopeAmount: 0,
    interestRate: 0,
    monthlyRepaymentAmount: 0,
    repaymentCount: 0,
    repaymentTtl: 0,
    interest: 0,
    repaymentPlanCount: 0,
  };
  await postSessionRePaySimulation(resetData);

  await navigateTo(
    `${convertUrl()}common/login/cushionWithUniversalLinkActivation?prompt=relogin&transitionSourceId=basic`,
    { external: true }
  );
};

watch(
  [session],
  ([newSession]) => {
    if (process.client) {
      if (newSession) {
        if (
          !newSession.rePaySimulationInfo?.hopeAmount ||
          !newSession.rePaySimulationInfo?.interestRate ||
          !newSession.rePaySimulationInfo?.monthlyRepaymentAmount ||
          !newSession.rePaySimulationInfo?.repaymentCount ||
          !newSession.rePaySimulationInfo?.repaymentTtl ||
          !newSession.rePaySimulationInfo?.interest
        ) {
          navigateTo(ROUTES.BORROW_SIMULATION.path);
        }
      }
    }
  },
  { immediate: true }
);
</script>

<template>
  <Header />
  <div class="isp hbg_bg"></div>
  <input type="hidden" id="screen_id" value="S14-12" />
  <input type="hidden" id="baseDomainPath" value="https://sp.loan.docomo.ne.jp" />

  <div id="app" class="container" style="visibility: visible">
    <section class="desired_amount_container">
      <div class="desired_amount_inner">
        <h1 class="desired_amount_title">シミュレーション結果</h1>
        <div class="desired_loan_area">
          <p class="desired_amount_subtitle">お借入希望額</p>
          <p class="desired_amount">
            <span id="desired_amount_money" class="desired_amount_money">{{ formattedAmount }}</span
            ><span id="desired_amount_money_tail" class="desired_amount_money_tail">円</span>
          </p>
        </div>
        <div class="desired_amount_interest_rate">
          <p class="desired_amount_subtitle"><span class="interest_rate_item">借入利率（実質年率）</span></p>
          <p class="interest_rate_percent">{{ formattedRate }} ％</p>
        </div>
      </div>
    </section>
    <main class="main_container">
      <section class="repayment_detail_container">
        <div class="repayment_detail_inner">
          <ui class="repayment_detail_list">
            <div class="border">
              <li>
                <p class="repayment_detail_item">月々のご返済額</p>
                <p class="repayment_detail_value">{{ formattedMonthlyRepaymentAmount }}円</p>
              </li>
            </div>
            <div class="border">
              <li>
                <p class="repayment_detail_item">ご返済回数</p>
                <p class="repayment_detail_value">{{ session?.rePaySimulationInfo?.repaymentCount }}回</p>
              </li>
            </div>
            <div class="border">
              <li>
                <p class="repayment_detail_item">ご返済総額</p>
                <p class="repayment_detail_value">{{ formattedTotalRepaymentAmount }}円</p>
              </li>
            </div>
            <div class="border">
              <li>
                <p class="repayment_detail_item">内）利息</p>
                <p class="repayment_detail_value">{{ formattedInterest }}円</p>
              </li>
            </div>
            <div class="border"></div>
          </ui>
        </div>
        <p class="description">
          ※上記シミュレーション結果は参考値です。お取引状況により実際のご返済内容とは異なる場合があります。また、お借入利率は審査の結果に応じて決定します。
        </p>
      </section>
      <section class="repayment_chart_container">
        <div class="repayment_chart_inner">
          <a class="repayment_chart_link" :href="ROUTES.BORROW_SIMULATION_PLAN.path">返済計画表を見る</a>
        </div>
      </section>
      <section class="btn_container">
        <div class="btn_inner">
          <button class="btn_rounded application_btn" @click="handleClickToApplyNow()">今すぐ申込む</button>
        </div>
      </section>
      <section class="btn_container top_page">
        <div class="btn_inner">
          <a href="/" class="btn_rounded top_page_btn">サイトトップへ</a>
        </div>
      </section>
    </main>
  </div>

  <Footer />
</template>

<style lang="scss" scoped>
@use '@/functions/borrow/simulation/views/css/simulation_result.css';
</style>
